import { useState } from 'react';

import { CalendarBookingModal } from '@backrs_ui/grow-ui/src/components/CalendarBookingModal/CalendarBookingModal';
import { UserProfileResponse } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, SxProps, Theme } from '@mui/material';

import { CoachDeepDivesSection } from './molecules/CoachDeepDivesSection';
import { CoachProfileSnapshot } from './molecules/CoachProfileSnapshot';
import { ProfileLinkedIn } from './molecules/ProfileLinkedIn';
import { PublicProfileHeader } from './molecules/PublicProfileHeader';
import { PublicProfileSnapshot } from './molecules/PublicProfileSnapshot';
import { PublicProfileStory } from './molecules/PublicProfileStory';

type PublicProfileProps = Readonly<{
  profile: UserProfileResponse & {
    purposeVideoUrl?: string;
  };
  userId?: number;
  isCoachingEnabled?: boolean;
}>;

export const PublicProfile = ({ profile, userId, isCoachingEnabled }: PublicProfileProps) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [contextProductId, setContextProductId] = useState<number | undefined>();
  const [bookingContext, setBookingContext] = useState<string | undefined>();

  const coaching = profile?.profile?.coaching;
  const hasCalendarLink = !!coaching?.consultation_calendar_url;
  const showCalendarModal =
    coaching?.consultation_calendar_url && coaching?.consultation_product_id && isBookingModalOpen;

  const handleScheduleConsultation = (ctxProductId?: number, ctxName?: string) => {
    setContextProductId(ctxProductId);
    setBookingContext(ctxName ?? 'Performance Coaching');
    setIsBookingModalOpen(true);
  };

  const handleBookingClose = () => {
    setIsBookingModalOpen(false);
    setContextProductId(undefined);
    setBookingContext(undefined);
  };

  return (
    <Box sx={pageContainerSx}>
      {/** HEADER */}
      <PublicProfileHeader
        publicProfile={profile}
        loggedInUserId={userId}
        onScheduleConsultation={hasCalendarLink ? handleScheduleConsultation : undefined}
        isCoachingEnabled={isCoachingEnabled}
      />

      <Box sx={contentContainerSx}>
        {coaching && isCoachingEnabled ? (
          <>
            {/** COACH SNAPSHOT */}
            <CoachProfileSnapshot coaching={coaching} purposeVideoUrl={profile.purposeVideoUrl} />

            {/** COACH DEEP DIVES */}
            <CoachDeepDivesSection
              deepDives={coaching.deep_dives || []}
              onScheduleConsultation={hasCalendarLink ? handleScheduleConsultation : undefined}
            />
          </>
        ) : (
          <>
            {/** SNAPSHOT */}
            <PublicProfileSnapshot publicProfile={profile} />

            {/** LINKEDIN */}
            {profile?.profile?.linkedInData && (
              <ProfileLinkedIn
                firstName={profile.profile?.publicProfile?.first_name || ''}
                linkedInData={profile?.profile.linkedInData}
              />
            )}

            {/** STORY */}
            <PublicProfileStory publicProfile={profile} />
          </>
        )}
      </Box>

      {showCalendarModal && (
        <CalendarBookingModal
          open={isBookingModalOpen}
          userId={userId}
          productId={coaching.consultation_product_id}
          coachProductId={coaching.consultation_coach_product_id}
          calendarUrl={coaching.consultation_calendar_url}
          contextProductId={contextProductId}
          bookingContext={bookingContext}
          onClose={handleBookingClose}
        />
      )}
    </Box>
  );
};

const pageContainerSx: SxProps<Theme> = {
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
};

const contentContainerSx: SxProps<Theme> = {
  margin: '0 auto',
  maxWidth: '1216px',
  '@media (max-width: 900px) and (max-height: 504px)': {
    maxWidth: '100%',
    marginLeft: '0px',
  },
  width: '100%',
  overflowX: 'hidden',
};
